import React, { useEffect, useState } from "react";
import { Trans, useTranslation } from "gatsby-plugin-react-i18next";
import "./pricing.css";

// Function to calculate the per-user cost dynamically for the one-time plan
const calculatePerUserCostOneTime = (numEmployees) => {
    if (numEmployees <= 149) {
        return 12.5;
    } else if (numEmployees <= 249) {
        return 10.5;
    } else {
        const baseCost = 9.5;
        const increments = Math.floor((numEmployees - 250) / 100);
        return Math.max(baseCost - 0.5 * increments, 6.0).toFixed(2);
    }
};

// Function to calculate the per-user cost dynamically for the standard plan
const calculatePerUserCostStandard = (numEmployees) => {
    if (numEmployees <= 149) {
        return 3.75;
    } else if (numEmployees <= 249) {
        return 3.5;
    } else {
        const baseCost = 3.25;
        const increments = Math.floor((numEmployees - 250) / 100);
        return Math.max(baseCost - 0.25 * increments, 1.5).toFixed(2);
    }
};

// Function to calculate the per-user cost dynamically for the enterprise plan
const calculatePerUserCostEnterprise = (numEmployees) => {
    // First, calculate the standard price
    const standardPrice = calculatePerUserCostStandard(numEmployees);

    // Then, increase the standard price by 25% to get the enterprise price
    const enterprisePrice = (standardPrice * 1.25).toFixed(2);

    return enterprisePrice;
};

const calculateCost = (numEmployees, planType) => {
    let perUserCost, totalCost;
    const setupCost = 250; // For one-time setup
    const minimumOneTimeCost = 1500;
    const minimumStandardCost = 4500;
    const minimumEnterpriseCost = 7500;

    if (planType === "oneTime") {
        if (numEmployees <= 100) {
            totalCost = minimumOneTimeCost;
            perUserCost = 12.5;
        } else {
            perUserCost = calculatePerUserCostOneTime(numEmployees);
            totalCost = Math.round(
                (numEmployees * perUserCost + setupCost).toFixed(2)
            );
        }
    } else if (planType === "standard") {
        if (numEmployees <= 100) {
            totalCost = minimumStandardCost; // Minimum standard cost for up to 100 users
            perUserCost = 3.75;
        } else {
            perUserCost = calculatePerUserCostStandard(numEmployees);
            totalCost = Math.round(
                (numEmployees * perUserCost * 12).toFixed(2)
            );
        }
    } else if (planType === "enterprise") {
        if (numEmployees <= 100) {
            totalCost = minimumEnterpriseCost;
            perUserCost = 5.25;
        } else {
            perUserCost = calculatePerUserCostEnterprise(numEmployees);
            totalCost = Math.round(
                (numEmployees * perUserCost * 12).toFixed(2)
            );
        }
    }

    return {
        costPerEmployee: perUserCost,
        totalCost: totalCost.toLocaleString(),
    };
};

// Main PricingSlider component
const PricingSlider = () => {
    const { t } = useTranslation();
    const [numEmployees, setNumEmployees] = useState(0);
    const [isThumbClicked, setIsThumbClicked] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState("annual");
    const [showContact, setShowContact] = useState(false);

    const getSliderColor = () => {
        const percentage = ((numEmployees - 0) / 1010) * 100;
        const greyPercentage = Math.min(percentage, 100);
        return `linear-gradient(to right, #f15162 ${greyPercentage}%, #42474d ${greyPercentage}%, #42474d 100%)`;
    };

    const handleChange = (event) => {
        const value = parseInt(event.target.value);
        if (value === 1010) {
            setNumEmployees(1000);
            setShowContact(true);
        } else {
            setNumEmployees(value);
            setShowContact(false);
        }
    };

    const handleThumbClick = () => {
        setIsThumbClicked(true);
    };

    const handleThumbRelease = () => {
        setIsThumbClicked(false);
    };

    const handleOneTimeClick = () => {
        setSelectedPlan("oneTime");
    };

    const handleAnnualClick = () => {
        setSelectedPlan("annual");
    };

    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        handleResize();

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    return (
        <>
            <section className="w-100 contact-pg-bg">
                <div className="contact-heading text-light container custom_container pricingheading">
                    <h1 className="font-weight-semibold py-2 section-heading sect-h1 text-white text-center">
                        <Trans>
                            Select the plan that best suits your goals!
                        </Trans>
                    </h1>
                    <div className="slider_area pricing-range-slider py-5 sect-white-col-2">
                        <div className="container custom_container price-cont-wrapper slider-wrapper">
                            <div className="estimate">
                                <Trans>Get an Estimate</Trans>
                            </div>
                            <p className="">
                                <Trans>
                                    Receive a price estimate customized to your
                                    requirements! Adjust the slider to indicate
                                    the anticipated number of users on your plan
                                    and instantly view a pricing quote in
                                    real-time.
                                </Trans>
                            </p>
                            <div className="no_emp">
                                <Trans>Number of users</Trans>
                            </div>
                            <div className="slider-container">
                                <div className="slider-value">
                                    <span className="empicon">
                                        <img
                                            src="https://res.cloudinary.com/vantagecircle/image/upload/v1709123823/VantageFit/2023/team.svg"
                                            alt="team icon"
                                        />
                                    </span>
                                    <span className="emp-value">
                                        {numEmployees === 1000 && !showContact
                                            ? "1000"
                                            : showContact
                                            ? "1000+"
                                            : numEmployees}
                                    </span>
                                </div>
                                <input
                                    type="range"
                                    min="0"
                                    max="1010" // Allow one extra step after 1000
                                    step="10"
                                    value={showContact ? 1010 : numEmployees} // Show 1000+ after 1000
                                    onChange={handleChange}
                                    className={`slider ${
                                        isThumbClicked ? "thumb-clicked" : ""
                                    }`}
                                    style={{ background: getSliderColor() }}
                                    onClick={handleThumbClick}
                                    onMouseUp={handleThumbRelease}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <section className="feat1_box py-5 pb-0">
                {!isMobile && (
                    <div className="toggle-container pb-5">
                        <div className="toggle-wrapper">
                            <button
                                onClick={handleOneTimeClick}
                                className={
                                    selectedPlan === "oneTime" ? "active" : ""
                                }
                            >
                                <Trans>One-time</Trans>
                            </button>
                            <button
                                onClick={handleAnnualClick}
                                className={
                                    selectedPlan === "annual" ? "active" : ""
                                }
                            >
                                <Trans>Annual</Trans>
                            </button>
                            <div className={`sliderbtn ${selectedPlan}`} />
                        </div>
                    </div>
                )}
                <div className="container custom_container price-cont-wrapper1">
                    <div className="col-12 my-4">
                        <div className="row">
                            {/* One-time Pricing */}
                            <div className="col-12 col-sm-12 col-md-6 col-lg-4">
                                <div
                                    className={`pricing-one-time ${
                                        selectedPlan !== "oneTime"
                                            ? "disabled-plan"
                                            : ""
                                    }`}
                                >
                                    <div className="pricing-type">
                                        <Trans>One-time</Trans>
                                    </div>
                                    {!showContact ? (
                                        <>
                                            <p className="starts">
                                                <Trans>Total Cost</Trans>
                                            </p>
                                            <div className="price-box">
                                                $
                                                {
                                                    calculateCost(
                                                        numEmployees,
                                                        "oneTime"
                                                    ).totalCost
                                                }
                                            </div>
                                            <div className="std-price">
                                                {numEmployees <= 99 && (
                                                    <>
                                                        <p
                                                            className="m-0 mt-1"
                                                            style={{
                                                                fontSize:
                                                                    "0.95rem",
                                                                opacity: "0",
                                                            }}
                                                        >
                                                            <Trans>Plus</Trans>
                                                        </p>
                                                        <p
                                                            className="m-0 mt-1"
                                                            style={{
                                                                fontSize:
                                                                    "0.95rem",
                                                            }}
                                                        >
                                                            <Trans>
                                                                Includes setup
                                                                cost
                                                            </Trans>
                                                        </p>
                                                    </>
                                                )}
                                                {numEmployees > 99 && (
                                                    <>
                                                        <p className="starts">
                                                            <strong>
                                                                $
                                                                {
                                                                    calculateCost(
                                                                        numEmployees,
                                                                        "oneTime"
                                                                    )
                                                                        .costPerEmployee
                                                                }
                                                            </strong>{" "}
                                                            /user
                                                        </p>
                                                        <p
                                                            className="m-0 mt-1"
                                                            style={{
                                                                fontSize:
                                                                    "0.95rem",
                                                                opacity: "0",
                                                            }}
                                                        >
                                                            <Trans>Plus</Trans>
                                                        </p>
                                                        <p
                                                            className="m-0 mt-1"
                                                            style={{
                                                                fontSize:
                                                                    "0.95rem",
                                                            }}
                                                        >
                                                            <Trans>
                                                                Includes setup
                                                                cost
                                                            </Trans>
                                                        </p>
                                                    </>
                                                )}
                                                <strong>
                                                    $
                                                    {numEmployees <= 99
                                                        ? 250
                                                        : 250}
                                                </strong>
                                                <br />
                                            </div>
                                            <div className="btn-wrapper">
                                                <a
                                                    href={t(`/request-demo/`)}
                                                    className="vfitcompanycta"
                                                >
                                                    <Trans>Book a Demo</Trans>
                                                </a>
                                            </div>
                                            <ul className="pricing-one-time-bullets">
                                                <li>
                                                    <Trans>
                                                        Choose any one challenge
                                                        from our 12 pre-built
                                                        templates
                                                    </Trans>
                                                </li>
                                                <li>
                                                    <Trans>
                                                        Team or individual
                                                        challenges
                                                    </Trans>
                                                </li>
                                                <li>
                                                    <Trans>
                                                        Incentivize tasks and
                                                        reward employees
                                                        directly from the app
                                                    </Trans>
                                                </li>
                                                <li
                                                    className="hidden-xs hidden-sm"
                                                    style={{ opacity: "0" }}
                                                >
                                                    <Trans>
                                                        On demand wellness
                                                        webinars
                                                    </Trans>
                                                </li>
                                                <li
                                                    className="hidden-xs hidden-sm"
                                                    style={{ opacity: "0" }}
                                                >
                                                    <Trans>
                                                        Customizable
                                                        communications
                                                    </Trans>
                                                </li>
                                                <li
                                                    className="hidden-xs hidden-sm"
                                                    style={{ opacity: "0" }}
                                                >
                                                    <Trans>
                                                        Execute multiple
                                                        challenges
                                                        simultaneously
                                                    </Trans>
                                                </li>
                                            </ul>
                                        </>
                                    ) : (
                                        <div className="contact-sales pt-3 pb-5">
                                            <p className="starts pb-3">
                                                <Trans>
                                                    Please contact sales for
                                                    pricing information for more
                                                    than 1000 users.
                                                </Trans>
                                            </p>
                                            <a
                                                href={t(`/contact-us/`)}
                                                className="vfitcompanycta mx-auto"
                                            >
                                                <Trans>Contact us</Trans>
                                            </a>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Standard Pricing */}
                            <div className="col-12 col-sm-12 col-md-6 col-lg-4">
                                <div
                                    className={`pricing-standard ${
                                        selectedPlan !== "annual"
                                            ? "disabled-plan"
                                            : ""
                                    }`}
                                >
                                    <div className="position-relative">
                                        <div className="popular-tag">
                                            <img
                                                className="mr-2"
                                                src="https://res.cloudinary.com/vantagecircle/image/upload/v1709123156/VantageFit/2023/flash.svg"
                                                alt="flash"
                                            />
                                            <div>
                                                <Trans>Popular</Trans>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="pricing-type">
                                        <Trans>Standard</Trans>
                                    </div>
                                    {!showContact ? (
                                        <>
                                            <p className="starts">
                                                <Trans>Total Annual Cost</Trans>
                                            </p>
                                            <div className="price-box">
                                                $
                                                {
                                                    calculateCost(
                                                        numEmployees,
                                                        "standard"
                                                    ).totalCost
                                                }
                                            </div>
                                            <div className="std-price">
                                                {numEmployees <= 99 && (
                                                    <>
                                                        <p
                                                            className="m-0 mt-1"
                                                            style={{
                                                                fontSize:
                                                                    "0.95rem",
                                                            }}
                                                        >
                                                            <Trans>Plus</Trans>
                                                        </p>
                                                    </>
                                                )}
                                                {numEmployees > 99 && (
                                                    <>
                                                        <p className="starts">
                                                            <strong>
                                                                $
                                                                {
                                                                    calculateCost(
                                                                        numEmployees,
                                                                        "standard"
                                                                    )
                                                                        .costPerEmployee
                                                                }
                                                            </strong>{" "}
                                                            /user per month
                                                        </p>
                                                        <p
                                                            className="m-0 mt-1"
                                                            style={{
                                                                fontSize:
                                                                    "0.95rem",
                                                            }}
                                                        >
                                                            <Trans>Plus</Trans>
                                                        </p>
                                                    </>
                                                )}

                                                <p
                                                    className="m-0 mt-1"
                                                    style={{
                                                        fontSize: "0.95rem",
                                                    }}
                                                >
                                                    <Trans>
                                                        One time setup cost
                                                    </Trans>
                                                </p>
                                                <strong>$500</strong>
                                                <br />
                                            </div>
                                            <div className="btn-wrapper">
                                                <a
                                                    href={t(`/request-demo/`)}
                                                    className="vfitcompanycta"
                                                >
                                                    <Trans>Book a Demo</Trans>
                                                </a>
                                            </div>
                                            <p className="text-left plustext">
                                                <Trans>
                                                    Get everything in One-time
                                                    plus
                                                </Trans>
                                            </p>
                                            <ul className="pricing-one-time-bullets pricing-standard-bullets">
                                                <li>
                                                    <Trans>
                                                        Unlimited Challenges
                                                    </Trans>
                                                </li>
                                                <li>
                                                    <Trans>
                                                        Create your own
                                                        customised challenge
                                                    </Trans>
                                                </li>
                                                <li>
                                                    <Trans>
                                                        Get access to analytics
                                                        of your wellness program
                                                    </Trans>
                                                </li>
                                                <li>
                                                    <Trans>
                                                        On demand wellness
                                                        webinars
                                                    </Trans>
                                                </li>
                                                <li>
                                                    <Trans>
                                                        Customizable
                                                        communications
                                                    </Trans>
                                                </li>
                                                <li>
                                                    <Trans>
                                                        Execute multiple
                                                        challenges
                                                        simultaneously
                                                    </Trans>
                                                </li>
                                            </ul>
                                        </>
                                    ) : (
                                        <div className="contact-sales pt-3 pb-5">
                                            <p className="starts pb-3">
                                                <Trans>
                                                    Please contact sales for
                                                    pricing information for more
                                                    than 1000 users.
                                                </Trans>
                                            </p>
                                            <a
                                                href={t(`/contact-us/`)}
                                                className="vfitcompanycta mx-auto"
                                            >
                                                <Trans>Contact us</Trans>
                                            </a>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Enterprise Pricing */}
                            <div className="col-12 col-sm-12 col-md-6 col-lg-4">
                                <div
                                    className={`pricing-premium ${
                                        selectedPlan !== "annual"
                                            ? "disabled-plan"
                                            : ""
                                    }`}
                                >
                                    <div className="pricing-type">
                                        <Trans>Enterprise</Trans>
                                    </div>
                                    {!showContact ? (
                                        <>
                                            <p className="starts">
                                                <Trans>Total Annual Cost</Trans>
                                            </p>
                                            <div className="price-box">
                                                $
                                                {
                                                    calculateCost(
                                                        numEmployees,
                                                        "enterprise"
                                                    ).totalCost
                                                }
                                            </div>
                                            <div className="std-price">
                                                {numEmployees <= 99 && (
                                                    <>
                                                        <p
                                                            className="m-0 mt-1"
                                                            style={{
                                                                fontSize:
                                                                    "0.95rem",
                                                            }}
                                                        >
                                                            <Trans>Plus</Trans>
                                                        </p>
                                                    </>
                                                )}
                                                {numEmployees > 99 && (
                                                    <>
                                                        <p className="starts">
                                                            <strong>
                                                                $
                                                                {
                                                                    calculateCost(
                                                                        numEmployees,
                                                                        "enterprise"
                                                                    )
                                                                        .costPerEmployee
                                                                }
                                                            </strong>{" "}
                                                            /user per month
                                                        </p>
                                                        <p
                                                            className="m-0 mt-1"
                                                            style={{
                                                                fontSize:
                                                                    "0.95rem",
                                                            }}
                                                        >
                                                            <Trans>Plus</Trans>
                                                        </p>
                                                    </>
                                                )}
                                                <p
                                                    className="m-0 mt-1"
                                                    style={{
                                                        fontSize: "0.95rem",
                                                    }}
                                                >
                                                    <Trans>
                                                        One time setup cost
                                                    </Trans>
                                                </p>
                                                <strong>$500</strong>
                                                <br />
                                            </div>
                                            <div className="btn-wrapper">
                                                <a
                                                    href={t(`/request-demo/`)}
                                                    className="vfitcompanycta"
                                                >
                                                    <Trans>Book a Demo</Trans>
                                                </a>
                                            </div>
                                            <p className="text-left plustext">
                                                <Trans>
                                                    Get everything in Standard
                                                    plus
                                                </Trans>
                                            </p>
                                            <ul className="pricing-one-time-bullets pricing-premium-bullets">
                                                <li>
                                                    <Trans>
                                                        Unlimited Challenges
                                                    </Trans>
                                                </li>
                                                <li>
                                                    <Trans>
                                                        Expert guided
                                                        implementation and
                                                        program planning
                                                    </Trans>
                                                </li>
                                                <li>
                                                    <Trans>
                                                        Quarterly surveys and
                                                        reviews
                                                    </Trans>
                                                </li>
                                                <li>
                                                    <Trans>
                                                        Customised company
                                                        timeline walkathon
                                                    </Trans>
                                                </li>
                                                <li>
                                                    <Trans>
                                                        Get access to SSO logins
                                                        and HRMS integrations
                                                    </Trans>
                                                </li>
                                                <li>
                                                    <Trans>
                                                        Unlock partner wellness
                                                        packages
                                                    </Trans>
                                                </li>
                                            </ul>
                                        </>
                                    ) : (
                                        <div className="contact-sales pt-3 pb-5">
                                            <p className="starts pb-3">
                                                <Trans>
                                                    Please contact sales for
                                                    pricing information for more
                                                    than 1000 users.
                                                </Trans>
                                            </p>
                                            <a
                                                href={t(`/contact-us/`)}
                                                className="vfitcompanycta mx-auto"
                                            >
                                                <Trans>Contact us</Trans>
                                            </a>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default PricingSlider;
